import { Project, ProjectLimitations } from "@microtica/ms-project-sdk";
import { CrownOutlined } from "@ant-design/icons";
import { PageHeader, Skeleton, Tabs } from "antd";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { getElasticSearchService, getEnvironmentService, getKubeService, getProjectService } from "../../backend";
import PageContentWrapper from "../../components/PageContentWrapper";
import PageHeaderBreadcrumb from "../../components/PageHeaderBreadcrumb";
import PageHeaderContent from "../../components/PageHeaderContent";
import { useCurrentProject } from "../../contexts/Project";
import { currentProjectState } from "../../recoil/project";
import { fetchProjectLimitations } from "../../utils/local-storage";
import CreateEnvironmentModal from "./CreateEnvironmentModal";
import MenuItemLabel from "../../components/MenuItemLabel";

const ProjectRoot = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { projectId } = useParams();
    const currentProject = useRecoilValue(currentProjectState);
    const { updateCurrentProject } = useCurrentProject();
    const [activeTab, setActiveTab] = useState("environments");
    const [projectLimitations, setProjectLimitations] = useState<ProjectLimitations>();
    const [projectDetails, setProjectDetails] = useState<Project>();
    const [totalEnvironments, setTotalEnvironments] = useState<number>();
    const [totalDeployedServices, setTotalDeployedServices] = useState<number>();
    const [buildMinutesUsed, setBuildMinutesUsed] = useState<number>();
    const [createEnvironmentModalVisible, setCreateEnvironmentModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchEnvironments = async () => {
        try {
            const { data: { stages } } = await getEnvironmentService().getStages(projectId!);
            setTotalEnvironments(stages.length);
        } catch (e) {
            setTotalEnvironments(undefined);
        }
    }

    const fetchBuildMinutes = async () => {
        try {
            const [{ data: { response: { buildTime } } }, { data: { response: { deploymentTime } } }] = await Promise.all([
                getElasticSearchService().getPipelineMetrics(projectId!),
                getElasticSearchService().getDeploymentTime(projectId!)
            ]);
            const MILLISECONDS_TO_MINUTES = 60000;
            setBuildMinutesUsed(Math.round((buildTime + deploymentTime) / (MILLISECONDS_TO_MINUTES)));
        } catch (e) {
            setBuildMinutesUsed(undefined);
        }
    }

    const fetchKubernetesServices = async () => {
        try {
            const { data: { microservices } } = await getKubeService().getDeployedMicroservicesInProject(projectId!);
            setTotalDeployedServices(microservices.length);
        } catch (e) {
            setTotalDeployedServices(undefined);
        }
    }

    const loadData = async () => {
        setLoading(true);
        const { data: project } = await getProjectService().getProject(projectId!);

        if (currentProject.id !== projectId) {
            await updateCurrentProject(projectId!);
        }

        setProjectDetails(project);

        await Promise.all([
            fetchEnvironments(),
            fetchBuildMinutes(),
            fetchKubernetesServices()
        ]);
        const limitations = fetchProjectLimitations();
        setProjectLimitations(limitations);
        setLoading(false);
    }

    useEffect(() => {
        if (location.pathname.split("/")[5] && activeTab !== location.pathname.split("/")[5]) {
            setActiveTab(location.pathname.split("/")[5]);
        }

        if (!projectDetails
            || projectId !== projectDetails.id
            || currentProject.name !== projectDetails.name
            || currentProject.description !== projectDetails.description) {
            // fetch data if there is inconsistency between the data in the local storage (currentProject) and
            // the data used in the Outlet (projectDetails) that is passed between the Project* components
            loadData();
        }
    }, [location.pathname]);


    const headerBreadcrumb = !loading ?
        <PageHeaderBreadcrumb
            items={[
                { link: "/projects", name: "Projects" },
                { link: `/projects/${projectId}`, name: currentProject?.name }
            ]}
        /> : <Skeleton.Button active={true} size="small" />;

    const headerTabs = (
        <Tabs
            activeKey={activeTab}
            onChange={(activeKey) => {
                navigate(activeKey);
                setActiveTab(activeKey)
            }}
        >
            <Tabs.TabPane tab="Environments" key="environments" />
            <Tabs.TabPane tab="Integrations" key="admin" />
            <Tabs.TabPane tab="Project Settings" key="settings" />
        </Tabs>
    );

    const actionItems = [
        {
            label: <MenuItemLabel
                text="Create Environment"
                showTooltip={(projectLimitations?.environmentsAllowed || 0) <= (totalEnvironments || 0)}
                tooltipText={projectLimitations?.environmentsAllowed ?
                    <>
                        Reached limit of {projectLimitations.environmentsAllowed} {projectLimitations.environmentsAllowed === 1 ? "environment." : "environments."}
                        <Link to="/pricing/upgrade" target="_blank">&nbsp;Upgrade plan</Link>
                    </> :
                    undefined
                }
            />,
            key: "create-environment",
            onClick: () => {
                setCreateEnvironmentModalVisible(true);
            },
            disabled: (projectLimitations?.environmentsAllowed || 0) <= (totalEnvironments || 0)
        },
        { type: "divider" },
        {
            label: "Billing",
            key: "billing",
            onClick: () => navigate(`/projects/${projectId}/settings/billing`)
        },
        {
            label: "Invite New Members",
            key: "invite-members",
            onClick: () => navigate(`/projects/${projectId}/settings/members`)
        },
        { type: "divider" },
        {
            label: "Connect Git Account",
            key: "connect-git",
            onClick: () => navigate(`/projects/${projectId}/admin/git`)
        },
        {
            label: "Connect AWS Account",
            key: "connect-aws",
            onClick: () => navigate(`/projects/${projectId}/admin/cloud`)
        },
        {
            label: "Connect Container Registry",
            key: "connect-registry",
            onClick: () => navigate(`/projects/${projectId}/admin/container`)
        }
    ] as ItemType[];

    const headerContent = !loading && projectDetails ? (
        <PageHeaderContent
            title={{
                prefix: "Project",
                mainText: projectDetails.name
            }}
            actionItems={actionItems}
            summary={[
                {
                    primaryText: projectDetails.paymentPlan && projectDetails.paymentPlan.name !== "Free" ?
                        <><CrownOutlined style={{ color: "goldenrod" }} /> {projectDetails.paymentPlan?.name}</> :
                        projectDetails.paymentPlan?.name
                },
                { secondaryText: !projectLimitations?.environmentsAllowed ? `${totalEnvironments} ${totalEnvironments === 1 ? "Environment" : "Environments"}` : `${totalEnvironments}/${projectLimitations.environmentsAllowed} Environments` },
                { secondaryText: !projectLimitations?.kubernetesServices ? `${totalDeployedServices} ${totalDeployedServices === 1 ? "App" : "Apps"}` : `${totalDeployedServices}/${projectLimitations.kubernetesServices} Apps` },
                { secondaryText: !projectLimitations?.buildMinutes ? `${buildMinutesUsed} ${buildMinutesUsed === 1 ? "Build Minute" : "Build Minutes"}` : `${buildMinutesUsed}/${projectLimitations?.buildMinutes} Build Minutes` }
            ]}
        />
    ) : <Skeleton.Input />

    return (
        <PageContentWrapper
            size="large"
            header={
                <PageHeader
                    title={headerBreadcrumb}
                    footer={headerTabs}
                    style={{ paddingTop: "8px" }}
                >
                    {headerContent}
                </PageHeader>
            }
            loading={loading}
        >
            {!loading ? <Outlet context={{ projectDetails }} /> : <Skeleton />}
            {createEnvironmentModalVisible &&
                <CreateEnvironmentModal
                    visible={createEnvironmentModalVisible}
                    onCancel={() => setCreateEnvironmentModalVisible(false)}
                    onOk={async () => {
                        await loadData();
                        setCreateEnvironmentModalVisible(false);
                    }}
                />}
        </PageContentWrapper>
    );
}

export default ProjectRoot;